import * as React from "react";
import { createContext, useContext, useEffect, useState, useCallback } from "react";
import type { User, Session, AuthError } from "@supabase/supabase-js";
import { supabase } from "@/lib/supabase";

/**
 * Auth Context Types
 */
type UserRole = "admin" | "member" | "viewer" | null;

interface AuthState {
  user: User | null;
  session: Session | null;
  loading: boolean;
  error: AuthError | null;
  role: UserRole;
}

interface AuthContextType extends AuthState {
  isAdmin: boolean;
  signIn: (email: string, password: string, rememberMe?: boolean) => Promise<void>;
  signUp: (email: string, password: string) => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  updatePassword: (newPassword: string) => Promise<void>;
  clearError: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

/**
 * Fetch the user's role from the profiles table
 * Uses type assertion since the role column is added by migration
 * and may not be reflected in generated types yet.
 */
async function fetchUserRole(userId: string): Promise<UserRole> {
  const { data } = await supabase.from("profiles").select("*").eq("id", userId).single();
  return ((data as Record<string, unknown> | null)?.role as UserRole) ?? "member";
}

/**
 * AuthProvider Component
 *
 * Wraps the application to provide authentication state and methods.
 * Uses Supabase Auth for session management.
 */
export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<AuthState>({
    user: null,
    session: null,
    loading: true,
    error: null,
    role: null,
  });

  // Effect 1: Auth state only — no Supabase queries inside the auth lock.
  // Calling supabase.from() inside onAuthStateChange/getSession causes a
  // deadlock because the inner request needs getSession() which is already locked.
  // See: https://supabase.com/docs/reference/javascript/auth-onauthstatechange
  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setState((prev) => ({
        ...prev,
        session,
        user: session?.user ?? null,
        loading: false,
      }));
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setState((prev) => ({
        ...prev,
        session,
        user: session?.user ?? null,
        loading: false,
      }));
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  // Effect 2: Fetch role when user changes — runs outside the auth lock
  const userId = state.user?.id ?? null;
  useEffect(() => {
    if (!userId) {
      setState((prev) => ({ ...prev, role: null }));
      return;
    }

    let cancelled = false;
    fetchUserRole(userId).then((role) => {
      if (!cancelled) {
        setState((prev) => ({ ...prev, role }));
      }
    });

    return () => {
      cancelled = true;
    };
  }, [userId]);

  /**
   * Sign in with email and password
   */
  const signIn = useCallback(async (email: string, password: string, _rememberMe = true) => {
    setState((prev) => ({ ...prev, loading: true, error: null }));

    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) {
      setState((prev) => ({ ...prev, loading: false, error }));
      throw error;
    }
  }, []);

  /**
   * Sign up with email and password
   */
  const signUp = useCallback(async (email: string, password: string) => {
    setState((prev) => ({ ...prev, loading: true, error: null }));

    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: `${window.location.origin}/`,
      },
    });

    if (error) {
      setState((prev) => ({ ...prev, loading: false, error }));
      throw error;
    }

    // Send welcome email (fire-and-forget, don't block signup)
    fetch("/api/email/welcome", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email }),
    }).catch((err) => {
      console.error("Failed to send welcome email:", err);
    });

    setState((prev) => ({ ...prev, loading: false }));
  }, []);

  /**
   * Sign in with Google OAuth
   */
  const signInWithGoogle = useCallback(async () => {
    setState((prev) => ({ ...prev, loading: true, error: null }));

    const { error } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: {
        redirectTo: `${window.location.origin}/dashboard`,
      },
    });

    if (error) {
      setState((prev) => ({ ...prev, loading: false, error }));
      throw error;
    }
  }, []);

  /**
   * Sign out the current user
   */
  const signOut = useCallback(async () => {
    setState((prev) => ({ ...prev, loading: true, error: null }));

    const { error } = await supabase.auth.signOut();

    if (error) {
      setState((prev) => ({ ...prev, loading: false, error }));
      throw error;
    }

    setState((prev) => ({
      ...prev,
      user: null,
      session: null,
      loading: false,
      role: null,
    }));
  }, []);

  /**
   * Send password reset email
   */
  const resetPassword = useCallback(async (email: string) => {
    setState((prev) => ({ ...prev, loading: true, error: null }));

    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });

    if (error) {
      setState((prev) => ({ ...prev, loading: false, error }));
      throw error;
    }

    setState((prev) => ({ ...prev, loading: false }));
  }, []);

  /**
   * Update password (after reset or from profile)
   */
  const updatePassword = useCallback(async (newPassword: string) => {
    setState((prev) => ({ ...prev, loading: true, error: null }));

    const { error } = await supabase.auth.updateUser({
      password: newPassword,
    });

    if (error) {
      setState((prev) => ({ ...prev, loading: false, error }));
      throw error;
    }

    setState((prev) => ({ ...prev, loading: false }));
  }, []);

  /**
   * Clear any auth errors
   */
  const clearError = useCallback(() => {
    setState((prev) => ({ ...prev, error: null }));
  }, []);

  const value: AuthContextType = {
    ...state,
    isAdmin: state.role === "admin",
    signIn,
    signUp,
    signInWithGoogle,
    signOut,
    resetPassword,
    updatePassword,
    clearError,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * Hook to access auth context
 * Must be used within an AuthProvider
 */
export function useAuthContext() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuthContext must be used within an AuthProvider");
  }
  return context;
}

export { AuthContext };
export type { AuthContextType, AuthState };
